import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { LocaleSwitcher } from '@/components/LocaleSwitcher';
import type { User } from '@/api/types';

function loginRedirectTarget(user: User): string {
  return user.role === 'platform_admin' || user.role === 'super_admin' ? '/platform' : '/';
}

interface FieldErrors {
  email?: string[];
  password?: string[];
}

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const { t } = useTranslation(['auth', 'common']);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setGeneralError(null);
    setFieldErrors({});
    setIsSubmitting(true);

    try {
      await login(email, password);
      const user = useAuthStore.getState().user;
      const target = user ? loginRedirectTarget(user) : '/';
      navigate(target, { replace: true });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 422) {
          const errors = err.response?.data?.errors as FieldErrors | undefined;
          if (errors) {
            setFieldErrors(errors);
          } else {
            setGeneralError(t('auth:login.errors.invalidCredentials'));
          }
        } else if (status === 403) {
          setGeneralError(t('auth:login.errors.accountInactive'));
        } else {
          setGeneralError(t('auth:login.errors.unexpected'));
        }
      } else {
        setGeneralError(t('auth:login.errors.unexpected'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = email.trim().length > 0 && password.length > 0;

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="absolute left-4 top-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          {t('common:actions.backToHome')}
        </Link>
      </div>
      <div className="absolute right-4 top-4">
        <LocaleSwitcher variant="auth" />
      </div>
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">{t('common:appName')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('auth:login.title')}</p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
          <form onSubmit={handleSubmit} noValidate>
            {generalError && (
              <div
                role="alert"
                className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {generalError}
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth:login.emailLabel')}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                aria-invalid={!!fieldErrors.email}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 aria-[invalid=true]:border-red-400"
                placeholder={t('auth:login.emailPlaceholder')}
                disabled={isSubmitting}
              />
              {fieldErrors.email && (
                <p id="email-error" className="mt-1 text-xs text-red-600">
                  {fieldErrors.email[0]}
                </p>
              )}
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('auth:login.passwordLabel')}
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline"
                >
                  {t('auth:login.forgotPassword')}
                </Link>
              </div>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                aria-invalid={!!fieldErrors.password}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 aria-[invalid=true]:border-red-400"
                placeholder="••••••••"
                disabled={isSubmitting}
              />
              {fieldErrors.password && (
                <p id="password-error" className="mt-1 text-xs text-red-600">
                  {fieldErrors.password[0]}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? t('auth:login.submitting') : t('auth:login.submit')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
